// Missile strikes in flight (docs/ART-DIRECTION.md §7 Flight): a neon ribbon over the part flown (launch city → the
// strike entity's interpolated position) and a dotted arc over the part left (→ where it is headed: its homing
// target's position now, read from its synced StrikeFlight every frame), both on the flight arc the strike's glyph
// rides (render/geo/strikeArc.ts), so a missile bending after a moving target stays on its ribbon. A flight ends when
// all its salvos resolved (impact or interception events), when its entity leaves the mirror, or when the mirror never
// shows it; it then fades out. Capacity is the sim's in-flight cap, so no quality tier ever drops a live strike's
// ribbon.

import { Vector3, type Color } from 'three'
import { LIMITS } from '@taxwar/content'
import { InvariantViolation, NEUTRAL_OWNER, NO_SIM_ID, type SimId, type StrikeTypeId } from '@taxwar/net'
import { GLOBE_ALTITUDE, latLonToVector3 } from '../render/geo/globeSpace'
import type { LatLonOut, MatchView, StrikeFlightOut } from '../match/contracts'
import { flightLift, strikeArcAt, type StrikeArcOut } from '../render/geo/strikeArc'
import type { FlightDef } from './effects'
import { MutablePathEnds, MIN_PATH_ANGLE, sampleFlightPiece } from './paths'
import type { FlightPlacement } from './placement'
import type { LastKnownPositions } from './positions'
import { RIBBON_POINTS, type RibbonPool } from './RibbonPool'

export const FLIGHT_CAPACITY = LIMITS.maxStrikesInFlight
/** Ribbon slots per flight: the travelled trail and the remaining dotted arc. */
export const FLIGHT_RIBBONS = 2
const TRAIL_RIBBON = 0
const ARC_RIBBON = 1
/** A ribbon whose span is shorter than this path fraction is not drawn this frame. */
const MIN_SPAN = 1e-3
/**
 * A followed strike the mirror has not shown for this long after launch (well past the interpolation buffer and a
 * view period) never will: it left this viewer's sight or was evicted from the position cache. Its flight fades.
 */
const SIGHTING_GRACE_SECONDS = 2
const XYZ = 3

const FlightPhase = { free: 0, flying: 1, fading: 2 } as const

export class FlightSystem {
  private readonly phases = new Uint8Array(FLIGHT_CAPACITY)
  private readonly defs: (FlightDef | null)[] = new Array<FlightDef | null>(FLIGHT_CAPACITY).fill(null)
  private readonly strikes = new Float64Array(FLIGHT_CAPACITY)
  private readonly salvosLeft = new Int32Array(FLIGHT_CAPACITY)
  /** Latest flown share of the whole flight; −1 until the strike was first seen. */
  private readonly progress = new Float32Array(FLIGHT_CAPACITY)
  /** Latest whole flight (flown + left), radians. */
  private readonly wholeAngles = new Float32Array(FLIGHT_CAPACITY)
  private readonly starts = new Float64Array(FLIGHT_CAPACITY)
  private readonly fadeStarts = new Float64Array(FLIGHT_CAPACITY)
  /** Unit normals of each flight's launch city and of where it is headed (a homing target moves). */
  private readonly origins: readonly Vector3[] = Array.from({ length: FLIGHT_CAPACITY }, () => new Vector3())
  private readonly targets: readonly Vector3[] = Array.from({ length: FLIGHT_CAPACITY }, () => new Vector3())
  /** The two pieces drawn: launch → the strike (flown), the strike → its target (left). */
  private readonly flownPaths: readonly MutablePathEnds[] = Array.from({ length: FLIGHT_CAPACITY }, () => new MutablePathEnds())
  private readonly leftPaths: readonly MutablePathEnds[] = Array.from({ length: FLIGHT_CAPACITY }, () => new MutablePathEnds())
  /** Live palette colours, kept by reference so a colour-blind toggle recolours flights in the air. */
  private readonly colours: (Color | null)[] = new Array<Color | null>(FLIGHT_CAPACITY).fill(null)
  /** Round-robin start of the slot search. */
  private cursor = 0
  private readonly points = new Float32Array(RIBBON_POINTS * XYZ)
  private readonly position: LatLonOut = { lat: 0, lon: 0 }
  private readonly here = new Vector3()
  private readonly flightOut: StrikeFlightOut = { owner: NEUTRAL_OWNER, strikeType: 0 as StrikeTypeId, originLat: 0, originLon: 0, targetLat: 0, targetLon: 0, flying: false }
  private readonly arc: StrikeArcOut = { flown: 0, left: 0, progress: 0 }

  /** Uses ribbon slots [firstSlot, firstSlot + FLIGHT_CAPACITY × FLIGHT_RIBBONS); reads where strikes are headed from `match`. */
  constructor(
    private readonly ribbons: RibbonPool,
    private readonly firstSlot: number,
    private readonly positions: LastKnownPositions,
    private readonly match: MatchView,
  ) {
    if (firstSlot + FLIGHT_CAPACITY * FLIGHT_RIBBONS > ribbons.capacity) throw new InvariantViolation('flight slots overflow the ribbon pool')
    this.progress.fill(-1)
  }

  /** `colour` is a live palette colour: kept by reference, never mutated. */
  play(def: FlightDef, placement: FlightPlacement, colour: Color, now: number): void {
    if (placement.strike === NO_SIM_ID) throw new InvariantViolation('flight effect played for a strike without a flight entity')
    if (placement.salvos < 1) throw new InvariantViolation(`flight effect played for a strike with ${placement.salvos} salvos`)
    // A strike sighted mid-flight may be adopted before its (late) strikeLaunched event, or vice versa: one flight each.
    if (this.find(placement.strike) >= 0) return
    const flight = this.claimFlight()
    if (this.phases[flight] !== FlightPhase.free) this.release(flight)
    // Origin = target leaves no arc to draw; the strike's impact effects still play.
    if (placement.origin.angleTo(placement.target) < MIN_PATH_ANGLE) return
    ;(this.origins[flight] as Vector3).copy(placement.origin)
    ;(this.targets[flight] as Vector3).copy(placement.target)
    this.phases[flight] = FlightPhase.flying
    this.defs[flight] = def
    this.strikes[flight] = placement.strike
    this.salvosLeft[flight] = placement.salvos
    this.progress[flight] = -1
    this.starts[flight] = now
    this.colours[flight] = colour
    this.positions.track(placement.strike)
  }

  /** An impact or interception resolved one of the strike's salvos; the last one ends the flight. */
  resolveSalvo(strike: SimId, now: number): void {
    const flight = this.find(strike)
    if (flight < 0 || this.phases[flight] !== FlightPhase.flying) return
    const left = (this.salvosLeft[flight] as number) - 1
    this.salvosLeft[flight] = left
    if (left <= 0) this.fade(flight, now)
  }

  /** Render radius of a followed strike on its arc, or null when this viewer does not follow it. */
  altitudeOf(strike: SimId): number | null {
    const flight = this.find(strike)
    if (flight < 0) return null
    const progress = this.progress[flight] as number
    if (progress < 0) return null
    return GLOBE_ALTITUDE.network + flightLift(this.wholeAngles[flight] as number, progress)
  }

  /** After LastKnownPositions.refresh(). */
  update(now: number): void {
    for (let flight = 0; flight < FLIGHT_CAPACITY; flight++) {
      const phase = this.phases[flight]
      if (phase === FlightPhase.free) continue
      const def = this.defs[flight] as FlightDef
      const strike = this.strikes[flight] as SimId
      if (phase === FlightPhase.flying) this.follow(flight, strike, now)
      let brightness = 1
      if (this.phases[flight] === FlightPhase.fading) {
        brightness = 1 - (now - (this.fadeStarts[flight] as number)) / def.fadeSeconds
        if (brightness <= 0) {
          this.release(flight)
          continue
        }
      }
      this.draw(flight, def, brightness)
    }
  }

  private follow(flight: number, strike: SimId, now: number): void {
    const state = this.positions.stateOf(strike, this.position)
    if (state === 'lost') {
      this.fade(flight, now)
      return
    }
    if (state === 'unknown') {
      if (now - (this.starts[flight] as number) >= SIGHTING_GRACE_SECONDS) this.fade(flight, now)
      return
    }
    const origin = this.origins[flight] as Vector3
    const target = this.targets[flight] as Vector3
    latLonToVector3(this.position.lat, this.position.lon, 1, this.here)
    // Where it is headed now: a homing strike's target moves. A strike the mirror no longer shows keeps its last one.
    if (this.match.strikeFlight(strike, this.flightOut)) latLonToVector3(this.flightOut.targetLat, this.flightOut.targetLon, 1, target)
    const arc = strikeArcAt(origin, this.here, target, this.arc)
    this.progress[flight] = arc.progress
    this.wholeAngles[flight] = arc.flown + arc.left
    ;(this.flownPaths[flight] as MutablePathEnds).set(origin, GLOBE_ALTITUDE.network, this.here, GLOBE_ALTITUDE.network)
    ;(this.leftPaths[flight] as MutablePathEnds).set(this.here, GLOBE_ALTITUDE.network, target, GLOBE_ALTITUDE.network)
  }

  private draw(flight: number, def: FlightDef, brightness: number): void {
    const progress = this.progress[flight] as number
    const whole = this.wholeAngles[flight] as number
    const trailSlot = this.firstSlot + flight * FLIGHT_RIBBONS + TRAIL_RIBBON
    const arcSlot = this.firstSlot + flight * FLIGHT_RIBBONS + ARC_RIBBON
    const flown = this.flownPaths[flight] as MutablePathEnds
    const left = this.leftPaths[flight] as MutablePathEnds
    const colour = this.colours[flight] as Color
    if (progress >= MIN_SPAN && flown.angle >= MIN_PATH_ANGLE) {
      sampleFlightPiece(flown, whole, 0, progress, this.points)
      this.ribbons.write(trailSlot, this.points, def.trail.style, colour, def.trail.intensity, brightness * def.trail.farAlpha, brightness, 0)
    } else {
      this.ribbons.hide(trailSlot)
    }
    if (progress >= 0 && 1 - progress >= MIN_SPAN && left.angle >= MIN_PATH_ANGLE) {
      // Dashes are placed from the launch city, so they hold still while the strike eats into them.
      sampleFlightPiece(left, whole, progress, 1, this.points)
      this.ribbons.write(arcSlot, this.points, def.arc.style, colour, def.arc.intensity, brightness, brightness * def.arc.farAlpha, flown.angle * GLOBE_ALTITUDE.network)
    } else {
      this.ribbons.hide(arcSlot)
    }
  }

  private fade(flight: number, now: number): void {
    this.phases[flight] = FlightPhase.fading
    this.fadeStarts[flight] = now
  }

  private release(flight: number): void {
    this.ribbons.hide(this.firstSlot + flight * FLIGHT_RIBBONS + TRAIL_RIBBON)
    this.ribbons.hide(this.firstSlot + flight * FLIGHT_RIBBONS + ARC_RIBBON)
    this.positions.forget(this.strikes[flight] as SimId)
    this.phases[flight] = FlightPhase.free
    this.defs[flight] = null
    this.colours[flight] = null
    this.strikes[flight] = NO_SIM_ID
  }

  /**
   * A free flight slot; else a fading one; else (every slot flying, only possible when a strike's resolution never
   * reached this viewer) the round-robin cursor's. Strikes resolve out of launch order, so plain round-robin would
   * recycle a live flight while others are free.
   */
  private claimFlight(): number {
    let fading = -1
    for (let n = 0; n < FLIGHT_CAPACITY; n++) {
      const flight = (this.cursor + n) % FLIGHT_CAPACITY
      const phase = this.phases[flight]
      if (phase === FlightPhase.free) return this.claimAt(flight)
      if (fading < 0 && phase === FlightPhase.fading) fading = flight
    }
    return this.claimAt(fading < 0 ? this.cursor : fading)
  }

  private claimAt(flight: number): number {
    this.cursor = (flight + 1) % FLIGHT_CAPACITY
    return flight
  }

  /** True while a flight (flying or fading) is assigned to this strike. */
  follows(strike: SimId): boolean {
    return this.find(strike) >= 0
  }

  private find(strike: SimId): number {
    if (strike === NO_SIM_ID) return -1
    for (let flight = 0; flight < FLIGHT_CAPACITY; flight++) {
      if (this.phases[flight] !== FlightPhase.free && this.strikes[flight] === strike) return flight
    }
    return -1
  }
}
